import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import VideoPlayer from './VideoPlayer';
import { toast } from 'react-hot-toast';

function StepDetailsModal({ step, onClose, onFileUploaded }) {
  const [statusTypes, setStatusTypes] = useState([]);
  const modalRef = useRef(null);
  
  // Add new state for loading state during downloads
  const [downloadingFileId, setDownloadingFileId] = useState(null);
  
  // Define fetchStatusTypes outside of useEffect
  const fetchStatusTypes = async () => {
    try {
      console.log('Fetching step status types for modal');
      // Get step status types
      const stepResponse = await axios.get('/api/settings/status-types/step');
      if (stepResponse.data && stepResponse.data.statusTypes) {
        setStatusTypes(stepResponse.data.statusTypes);
        console.log('Step status types loaded in modal:', stepResponse.data.statusTypes);
      }
    } catch (error) {
      console.error('Error fetching status types for modal:', error);
      console.error('Status types error response:', error.response?.data);
      console.error('Status types error status:', error.response?.status);
      // Non-critical error, don't need to show to user
    }
  };
  
  // Fetch status types on mount
  useEffect(() => {
    fetchStatusTypes();
  }, []);
  
  // Handle click outside to close modal
  useEffect(() => {
    // Add click outside listener
    function handleClickOutside(event) {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);
  
  // Get the status color
  const getStatusColor = (statusName) => {
    const statusType = statusTypes.find(s => s.name === statusName);
    return statusType?.color || '#6B7280';
  };
  
  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Add a function to handle file downloads
  const handleDownload = async (fileId, fileName) => {
    try {
      setDownloadingFileId(fileId);
      
      const response = await axios.get(`/api/client-portal/files/download/${fileId}`);
      
      if (response.data && response.data.success && response.data.downloadUrl) {
        // Create a temporary link element
        const link = document.createElement('a');
        link.href = response.data.downloadUrl;
        link.setAttribute('download', response.data.filename || fileName);
        link.setAttribute('target', '_blank');
        
        // Trigger a click on this link to start the download
        document.body.appendChild(link);
        link.click();
        
        // Clean up by removing the link
        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);
      } else {
        throw new Error('Invalid download response');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file. Please try again.');
    } finally {
      setDownloadingFileId(null);
    }
  };
  
  return (
    <div className="fixed inset-0 z-10 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        
        <div className="inline-block px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full sm:p-6">
          <div>
            <div className="flex justify-between items-center">
              <div>
                {/* Project Info */}
                <h3 className="text-lg font-medium leading-6 text-gray-900">
                  {step.name || 'Step Details'}
                </h3>
              </div>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-500"
                onClick={onClose}
              >
                <span className="sr-only">Close</span>
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Status Badge */}
            <div className="mt-2 flex items-center">
              <span
                className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full items-center justify-center"
                style={{
                  backgroundColor: getStatusColor(step.status || 'Not Started'),
                  color: 'white',
                  minWidth: '80px',
                  textAlign: 'center'
                }}
              >
                {step.status || 'Not Started'}
              </span>
              
              {/* Updated Date */}
              {step.updated_at && (
                <span className="ml-2 text-xs text-gray-500">
                  Updated: {formatDate(step.updated_at)}
                </span>
              )}
            </div>
            
            {/* Due Date */}
            {step.due_date && (
              <div className="mt-2 flex items-center">
                <svg className="h-4 w-4 text-orange-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-sm font-medium text-gray-900">
                  Due: <span className="text-orange-600">{new Date(step.due_date).toLocaleDateString()}</span>
                </span>
              </div>
            )}
            
            {/* Description */}
            <div className="mt-4 p-4 bg-gray-50 rounded-md">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Description</h4>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {step.description || 'No description provided.'}
              </p>
            </div>
            
            {/* Instructional Video */}
            {step.video_url && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Instructional Video</h4>
                <div className="aspect-w-16 aspect-h-9 bg-gray-100 rounded-md overflow-hidden">
                  <VideoPlayer url={step.video_url} />
                </div>
              </div>
            )}
            
            {/* Files - Read Only */}
            <div className="mt-6">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-sm font-medium text-gray-900">Files</h4>
              </div>
              <div className="max-h-40 overflow-y-auto pr-1">
                {step.files?.length > 0 ? (
                  <ul className="divide-y divide-gray-200">
                    {step.files.map((file, index) => (
                      <li key={file.id || index} className="py-3 flex justify-between items-center">
                        <div className="flex items-center">
                          <svg className="h-5 w-5 text-gray-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                          </svg>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{file.original_filename}</p>
                            <p className="text-xs text-gray-500">
                              {formatDate(file.created_at)} • 
                              {(file.file_size / 1024).toFixed(1)} KB
                            </p>
                          </div>
                        </div>
                        <a
                          onClick={() => handleDownload(file.id, file.original_filename)}
                          className="text-blue-600 hover:text-blue-900 text-sm cursor-pointer"
                        >
                          {downloadingFileId === file.id ? 'Downloading...' : 'Download'}
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500 italic text-center py-4">No files available for this step.</p>
                )}
              </div>
            </div>
            
            {/* Info about central file uploader */}
            <div className="mt-6 bg-blue-50 p-4 rounded-md">
              <h3 className="text-base font-medium text-blue-800 mb-2">
                Need to upload files?
              </h3>
              <p className="text-sm text-blue-700">
                You can upload files for this step using the central file uploader in the "Upload Files" tab.
              </p>
            </div>
            
            {/* Notes - Read Only */}
            <div className="mt-6">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-sm font-medium text-gray-900">Notes</h4>
              </div>
              
              {/* Notes list */}
              <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
                {step.notes?.length > 0 ? (
                  step.notes.map((note, index) => (
                    <div key={note.id || index} className="p-4 bg-gray-50 rounded-md">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-900">
                          {note.created_by_name || 'Staff'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDate(note.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">
                        {note.content}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 italic text-center py-4">No notes for this step.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StepDetailsModal; 